import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Trash2, Palette, Type, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

export default function BrandingSection({ eventId, permissions }: { eventId: string; permissions: any }) {
  const [branding, setBranding] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    asset_type: 'logo',
    name: '',
    url: '',
    description: '',
    hex_color: '',
    font_name: ''
  });

  useEffect(() => {
    loadBranding();
  }, [eventId]);

  const loadBranding = async () => {
    const { data } = await supabase
      .from('branding_assets')
      .select('*')
      .eq('event_id', eventId)
      .order('asset_type, name');
    if (data) setBranding(data);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      await supabase
        .from('branding_assets')
        .update(formData)
        .eq('id', editingId);
    } else {
      await supabase
        .from('branding_assets')
        .insert({ ...formData, event_id: eventId });
    }
    resetForm();
    loadBranding();
  };

  const deleteEntry = async (id: string) => {
    await supabase.from('branding_assets').delete().eq('id', id);
    setDeleteId(null);
    loadBranding();
  };

  const resetForm = () => {
    setFormData({
      asset_type: 'logo',
      name: '',
      url: '',
      description: '',
      hex_color: '',
      font_name: ''
    });
    setShowForm(false);
    setEditingId(null);
  };

  const editEntry = (entry: any) => {
    setFormData(entry);
    setEditingId(entry.id);
    setShowForm(true);
  };

  const assetTypes = [
    { value: 'logo', label: 'Logo', icon: ImageIcon },
    { value: 'color', label: 'Color Palette', icon: Palette },
    { value: 'font', label: 'Typography', icon: Type }
  ];

  const groupedBranding = assetTypes.map(type => ({
    ...type,
    items: branding.filter(b => b.asset_type === type.value)
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Branding</h2>
          <p className="text-zinc-500 mt-1">Logos, colors, and typography</p>
        </div>
        {permissions.canEdit && (
          <Button onClick={() => setShowForm(!showForm)} className="bg-white text-zinc-900 hover:bg-zinc-200">
            <Plus className="w-4 h-4 mr-2" />
            Add Asset
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Edit' : 'Add'} Branding Asset
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="asset_type" className="text-zinc-300">Asset Type *</Label>
                <select
                  id="asset_type"
                  value={formData.asset_type}
                  onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })}
                  required
                  className="mt-1.5 w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2"
                >
                  {assetTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="name" className="text-zinc-300">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Primary Logo"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            </div>

            {formData.asset_type !== 'color' && (
              <div>
                <Label htmlFor="url" className="text-zinc-300">
                  {formData.asset_type === 'logo' ? 'Logo URL' : 'Font URL'}
                </Label>
                <Input
                  id="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://..."
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            )}

            {formData.asset_type === 'color' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="hex_color" className="text-zinc-300">Hex Color</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input
                      id="hex_color"
                      value={formData.hex_color}
                      onChange={(e) => setFormData({ ...formData, hex_color: e.target.value })}
                      placeholder="#000000"
                      className="bg-zinc-800/50 border-zinc-700 text-white"
                    />
                    <input
                      type="color"
                      value={formData.hex_color || '#000000'}
                      onChange={(e) => setFormData({ ...formData, hex_color: e.target.value })}
                      className="w-12 h-10 bg-zinc-800 border border-zinc-700 rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.asset_type === 'font' && (
              <div>
                <Label htmlFor="font_name" className="text-zinc-300">Font Name</Label>
                <Input
                  id="font_name"
                  value={formData.font_name}
                  onChange={(e) => setFormData({ ...formData, font_name: e.target.value })}
                  placeholder="e.g., Inter, Helvetica Neue"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            )}

            <div>
              <Label htmlFor="description" className="text-zinc-300">Description / Usage</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                placeholder="Usage notes, specifications, etc."
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" className="bg-white text-zinc-900 hover:bg-zinc-200">
                {editingId ? 'Update' : 'Add'} Asset
              </Button>
              <Button type="button" variant="ghost" onClick={resetForm} className="text-zinc-400 hover:text-white">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-zinc-500">Loading...</div>
      ) : branding.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-8 text-center">
          <p className="text-zinc-500">No branding assets added yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupedBranding.map((type) => (
            type.items.length > 0 && (
              <div key={type.value}>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <type.icon className="w-5 h-5" />
                  {type.label}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {type.items.map((asset) => (
                    <div key={asset.id} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-5 hover:border-zinc-700/50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-base font-medium text-white">{asset.name}</h4>
                        {permissions.canEdit && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => editEntry(asset)}
                              className="text-zinc-400 hover:text-white h-7 px-2 text-xs"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteId(asset.id)}
                              className="text-zinc-400 hover:text-red-400 h-7 w-7"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {asset.asset_type === 'logo' && asset.url && (
                        <div className="bg-white rounded-lg p-4 mb-3 flex items-center justify-center min-h-[120px]">
                          <img src={asset.url} alt={asset.name} className="max-w-full max-h-[100px] object-contain" />
                        </div>
                      )}

                      {asset.asset_type === 'color' && asset.hex_color && (
                        <div className="mb-3">
                          <div
                            className="w-full h-20 rounded-lg border border-zinc-700"
                            style={{ backgroundColor: asset.hex_color }}
                          />
                          <p className="text-zinc-400 text-sm mt-2 font-mono">{asset.hex_color}</p>
                        </div>
                      )}

                      {asset.asset_type === 'font' && asset.font_name && (
                        <div className="mb-3 p-4 bg-zinc-800/50 rounded-lg">
                          <p className="text-white text-2xl" style={{ fontFamily: asset.font_name }}>
                            Aa Bb Cc
                          </p>
                          <p className="text-zinc-500 text-sm mt-1">{asset.font_name}</p>
                        </div>
                      )}

                      {asset.url && asset.asset_type !== 'logo' && (
                        <a
                          href={asset.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-400 hover:text-primary-300 block mb-2"
                        >
                          View Resource →
                        </a>
                      )}

                      {asset.description && (
                        <p className="text-sm text-zinc-500">{asset.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Branding Asset?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove this branding asset.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteEntry(deleteId)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
